// SATI ARCHITECTS — Project pricing & configurator (single source of truth)
// Homepage teaser and /estimate configurator both derive from the same core.
// Values are indicative only — not a commercial offer.

/** Shown under headline figures (teaser, summary, email) */
export const ESTIMATE_PRICE_CONTEXT_UA =
  'Розрахунок є орієнтовним. Фінальна вартість залежить від площі, складу приміщень, стилю та складності проєкту.';

export const ESTIMATE_PRICE_CONTEXT_EN =
  'This estimate is indicative. Final cost depends on area, scope, style, and project complexity.';

/**
 * Indicative USD band around a midpoint (avoids false precision).
 * Same function drives teaser, configurator summary, and email.
 */
export function computeEstimateRange(baseValue) {
  const n = Number(baseValue);
  if (!Number.isFinite(n) || n <= 0) return { min: 0, max: 0 };
  return {
    min: Math.round(n * 0.9),
    max: Math.round(n * 1.15),
  };
}

export function formatUsdRange(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || (min <= 0 && max <= 0)) return '—';
  return `$${min.toLocaleString()} \u2013 $${max.toLocaleString()}`;
}

export function formatTotalAsRange(total) {
  const { min, max } = computeEstimateRange(total);
  return formatUsdRange(min, max);
}

/** Area slider / inputs (m²) */
export const AREA_BOUNDS = { min: 20, max: 500, default: 80 };

/** USD per m² by finish level (design-project fee model) */
export const pricingConfig = {
  levels: {
    base: 20,
    premium: 35,
    luxury: 50,
  },
};

/**
 * Multiplier on (area × rate) by typology. Editorial coefficients — adjust in one place only.
 */
export const projectTypeRateMultipliers = {
  apartment: 1,
  house: 1.08,
  commercial: 1.12,
};

export const projectTypes = [
  { id: 'apartment', labelUA: 'Квартира', labelEN: 'Apartment' },
  { id: 'house', labelUA: 'Будинок', labelEN: 'House' },
  { id: 'commercial', labelUA: 'Комерційне', labelEN: 'Commercial' },
];

export const qualityLevels = [
  { id: 'base', labelUA: 'Базовий', labelEN: 'Base' },
  { id: 'premium', labelUA: 'Преміум', labelEN: 'Premium' },
  { id: 'luxury', labelUA: 'Люкс', labelEN: 'Luxury' },
];

/** Configurator — design direction factors applied after base + rooms */
export const stylePriceFactors = {
  minimal: 1,
  classic: 1.05,
  contemporary: 1.08,
  eclectic: 1.06,
};

export const DESIGN_STYLES = [
  { id: 'minimal', labelUA: 'Мінімалізм', labelEN: 'Minimal' },
  { id: 'classic', labelUA: 'Класика', labelEN: 'Classic' },
  { id: 'contemporary', labelUA: 'Сучасний', labelEN: 'Contemporary' },
  { id: 'eclectic', labelUA: 'Еклектика', labelEN: 'Eclectic' },
];

/** Flat add-ons (USD) when selected on configurator */
export const configuratorExtraFees = {
  authorSupervision: 2500,
  visualizationPack: 1800,
  furnitureSelection: 1200,
};

export const CONFIGURATOR_EXTRAS_META = [
  {
    key: 'authorSupervision',
    labelUA: 'Авторський нагляд',
    labelEN: 'Author supervision',
  },
  {
    key: 'visualizationPack',
    labelUA: 'Розширений пакет візуалізацій',
    labelEN: 'Extended visualization pack',
  },
  {
    key: 'furnitureSelection',
    labelUA: 'Підбір меблів та матеріалів',
    labelEN: 'Furniture & materials selection',
  },
];

const ROOM_INCLUDED_BEDROOMS = 2;
const ROOM_INCLUDED_BATHROOMS = 1;
const FEE_PER_BEDROOM_OVER_INCLUDED = 400;
const FEE_PER_BATHROOM_OVER_INCLUDED = 350;

export function clampArea(area) {
  const n = Number(area);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(AREA_BOUNDS.max, Math.max(AREA_BOUNDS.min, n));
}

/**
 * Shared core: (area × finish rate × typology multiplier).
 * Homepage teaser uses this only; configurator builds further on top.
 */
export function computeBaseDesignSubtotal({ projectType, quality, area }) {
  const rate = pricingConfig.levels[quality];
  if (!rate) return null;
  const areaM2 = clampArea(area);
  if (areaM2 <= 0) return null;
  const typeMultiplier = projectTypeRateMultipliers[projectType] ?? 1;
  return {
    subtotal: areaM2 * rate * typeMultiplier,
    ratePerSqm: rate,
    areaM2,
    typeMultiplier,
  };
}

/**
 * Homepage estimator — indicative total from typology + finish + area (no rooms/style/extras).
 */
export function calculateTeaserTotal({ projectType, quality, area }) {
  const base = computeBaseDesignSubtotal({ projectType, quality, area });
  if (!base) return null;
  return {
    total: Math.round(base.subtotal),
    ratePerSqm: base.ratePerSqm,
    areaM2: base.areaM2,
    typeMultiplier: base.typeMultiplier,
  };
}

/**
 * @deprecated Prefer calculateTeaserTotal({ projectType, quality, area }). Kept for any external imports.
 */
export function calculateEstimate(quality, area) {
  return calculateTeaserTotal({ projectType: 'apartment', quality, area });
}

/**
 * Full configurator breakdown — same base core as teaser, then rooms, style factor, extras.
 */
export function calculateConfiguratorPrice(state) {
  const level = state.level || 'base';
  const rateFallback = pricingConfig.levels[level] ?? pricingConfig.levels.base;

  const base = computeBaseDesignSubtotal({
    projectType: state.projectType,
    quality: level,
    area: state.area,
  });

  if (!base) {
    return {
      total: 0,
      baseFromArea: 0,
      roomSurcharge: 0,
      subtotalBeforeStyle: 0,
      styleMultiplier: 1,
      styleAdjustment: 0,
      afterStyle: 0,
      extrasTotal: 0,
      extrasLineItems: [],
      ratePerSqm: rateFallback,
      areaM2: clampArea(state.area) || 0,
      typeMultiplier: projectTypeRateMultipliers[state.projectType] ?? 1,
    };
  }

  const { subtotal: baseFromArea, ratePerSqm, areaM2, typeMultiplier } = base;

  const bedN = Math.max(0, Number(state.bedrooms) || 0);
  const bathN = Math.max(0, Number(state.bathrooms) || 0);
  const roomSurcharge =
    Math.max(0, bedN - ROOM_INCLUDED_BEDROOMS) * FEE_PER_BEDROOM_OVER_INCLUDED +
    Math.max(0, bathN - ROOM_INCLUDED_BATHROOMS) * FEE_PER_BATHROOM_OVER_INCLUDED;

  const subtotalBeforeStyle = baseFromArea + roomSurcharge;
  const styleMultiplier = stylePriceFactors[state.style] ?? 1;
  const afterStyle = subtotalBeforeStyle * styleMultiplier;
  const styleAdjustment = afterStyle - subtotalBeforeStyle;

  const extras = state.extras || {};
  const extrasLineItems = [];
  let extrasTotal = 0;
  Object.entries(configuratorExtraFees).forEach(([key, amount]) => {
    if (extras[key]) {
      extrasTotal += amount;
      extrasLineItems.push({ key, amount });
    }
  });

  const total = Math.round(afterStyle + extrasTotal);

  return {
    total,
    baseFromArea,
    roomSurcharge,
    subtotalBeforeStyle,
    styleMultiplier,
    styleAdjustment,
    afterStyle,
    extrasTotal,
    extrasLineItems,
    ratePerSqm,
    areaM2,
    typeMultiplier,
  };
}
